// A single service card: brand icon + title + (optional tagline) + description,
// with an optional bullet list of detail points. Hover lift + tint micro-motion.
// Owned by Agent D.
import type { ReactNode } from "react";
import { motion } from "framer-motion";

import { fadeUp } from "../motion/variants";

export type ServiceIcon = "container" | "vessel" | "forwarding" | "trade";

const icons: Record<ServiceIcon, ReactNode> = {
  // Stacked shipping containers.
  container: (
    <>
      <rect x="3" y="13" width="8.5" height="7" rx="1" />
      <rect x="12.5" y="13" width="8.5" height="7" rx="1" />
      <rect x="7.5" y="5" width="9" height="7" rx="1" />
      <path d="M5 16.5h4.5M14.5 16.5H19M9.5 8.5h5" strokeWidth="1.2" />
    </>
  ),
  // Sailing/cargo vessel on a wave.
  vessel: (
    <>
      <path d="M3 16h18l-2.2 4H5.2L3 16Z" />
      <path d="M12 4v9M12 5.5 18 13h-6M12 7l-4.5 6H12" strokeWidth="1.4" />
      <path d="M2 20.5c1.6 1 3.2 1 4.8 0s3.2-1 4.8 0 3.2 1 4.8 0 3.2-1 4.8 0" strokeWidth="1.4" fill="none" />
    </>
  ),
  // Document + route arrow (forwarding / customs).
  forwarding: (
    <>
      <path d="M6 3h7l5 5v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" />
      <path d="M13 3v5h5" strokeWidth="1.2" />
      <path d="M7.5 13h6.5m0 0-2-2m2 2-2 2" strokeWidth="1.4" />
    </>
  ),
  // Globe with a network node (trade / cross-border e-commerce).
  trade: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17M12 3.5c2.6 2.3 2.6 14.7 0 17M12 3.5c-2.6 2.3-2.6 14.7 0 17" strokeWidth="1.2" fill="none" />
    </>
  ),
};

export interface ServiceCardProps {
  icon: ServiceIcon;
  title: string;
  tagline?: string;
  desc: string;
  points?: string[];
  /** Variant: compact (home grid) or detailed (services page). */
  variant?: "compact" | "detailed";
}

export default function ServiceCard({
  icon,
  title,
  tagline,
  desc,
  points,
  variant = "compact",
}: ServiceCardProps) {
  return (
    <motion.article
      variants={fadeUp}
      className="group flex h-full flex-col rounded-[var(--radius-lg)] border border-[var(--brand-tint)] bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-[var(--brand-tint-2)] hover:shadow-[var(--shadow-card)]"
    >
      <span className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-[var(--radius-md)] bg-[var(--brand-tint-3)] text-[var(--brand-azure)] transition-colors group-hover:bg-[var(--brand-primary)] group-hover:text-white">
        <svg
          viewBox="0 0 24 24"
          className="h-7 w-7"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          {icons[icon]}
        </svg>
      </span>

      <h3 className="font-zh text-[var(--text-xl)] font-bold text-[var(--ink)]">{title}</h3>
      {tagline && (
        <p className="mt-1 text-[var(--text-sm)] font-medium text-[var(--brand-primary-text)]">
          {tagline}
        </p>
      )}
      <p className="mt-3 text-[var(--text-base)] leading-[var(--leading-body)] text-[var(--brand-slate-text)]">
        {desc}
      </p>

      {variant === "detailed" && points && points.length > 0 && (
        <ul className="mt-5 space-y-2.5 border-t border-[var(--brand-tint)] pt-5">
          {points.map((p) => (
            <li key={p} className="flex gap-2.5 text-[var(--text-sm)] text-[var(--ink)]/80">
              <svg
                viewBox="0 0 16 16"
                className="mt-1 h-3.5 w-3.5 flex-none text-[var(--brand-primary)]"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M3 8.5l3.5 3.5L13 4.5" />
              </svg>
              <span>{p}</span>
            </li>
          ))}
        </ul>
      )}
    </motion.article>
  );
}
